import React, { useState } from 'react';

import { DATA_TYPE_INFO } from "@dndModels/Constants";

import MyTTRPGAPI from "@utilities/smart_api";
import { SourceBookContent } from "@utilities/smart_api_class";

import CheckBox from "@components/input/CheckBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

const pluralFor = (data_type: string) =>
  DATA_TYPE_INFO.find(o => o.data_type === data_type)?.plural ?? data_type;

// Makes things that are in one source book also be in another (optionally taking them out of the first).
// Non-admins can only do this from one of their own source books to another.
export default function SourceBookCopyTool() {
  const [open, setOpen] = useState(false);
  const [from_id, setFromId] = useState("");
  const [to_id, setToId] = useState("");
  const [contents, setContents] = useState<SourceBookContent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [remove_from_old, setRemoveFromOld] = useState(false);
  const [saving, setSaving] = useState<{ done: number, total: number, failed: string[] } | null>(null);

  const user = api.user;
  const options = user && user.admin ? api.source_books : api.owned_source_books;
  const from_book = options.find(o => o._id === from_id);
  const to_book = options.find(o => o._id === to_id);
  // Everything in the original that isn't already in the destination (and for non-admins, that they own).
  const candidates = (contents ?? []).filter(o => !o.source_books_old.some(sb => sb.id === to_id) &&
    (user === null || user.admin || o.owner_id === user._id));
  const keyOf = (o: SourceBookContent) => `${o.data_type}|${o._id}`;
  const chosen = candidates.filter(o => selected.includes(keyOf(o)));
  const running = !!saving && saving.done < saving.total;

  const loadFrom = async (id: string) => {
    setFromId(id);
    setSelected([]);
    setSaving(null);
    setContents(null);
    if (id !== "") {
      setLoading(true);
      setContents(await api.findSourceBookContents(id));
      setLoading(false);
    }
  }

  const copy = async () => {
    if (!to_book) {
      return;
    }
    const list = [...chosen];
    const failed: string[] = [];
    setSaving({ done: 0, total: list.length, failed });
    for (let i = 0; i < list.length; ++i) {
      const content = list[i];
      let source_books = [
        ...content.source_books_old,
        { id: to_book._id, type: to_book.source_book_type, third_party: to_book.third_party }
      ];
      if (remove_from_old) {
        source_books = source_books.filter(o => o.id !== from_id);
      }
      try {
        if (!(await api.setContentSourceBooks(content, source_books))) {
          failed.push(content.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(content.name);
      }
      setSaving({ done: i + 1, total: list.length, failed: [...failed] });
    }
    setSelected([]);
    // Reload so what's moved drops off the list.
    setContents(await api.findSourceBookContents(from_id));
  }

  if (!user || !api.online || options.length === 0) {
    return null;
  } else if (!open) {
    return (
      <FlexBox>
        <DandDButtonBox name={`Copy Between${ user.admin ? "" : " Your"} Source Books`} onClick={() => setOpen(true)} />
      </FlexBox>
    );
  }

  const groups: { data_type: string, items: SourceBookContent[] }[] = [];
  for (const content of candidates) {
    const group = groups.find(o => o.data_type === content.data_type);
    if (group) {
      group.items.push(content);
    } else {
      groups.push({ data_type: content.data_type, items: [content] });
    }
  }
  const toggle = (keys: string[], on: boolean) => {
    setSelected(on ? [...selected.filter(o => !keys.includes(o)), ...keys] : selected.filter(o => !keys.includes(o)));
  }

  return (
    <FlexBox direction="column" className="padded" style={{ border: "1px solid rgba(128,128,128,0.5)", borderRadius: 4 }}>
      <span className="header">Copy Between Source Books</span>
      <span>
        Choose things in one source book to also put in another.
        { !user.admin && " You can only do this between source books you own." }
      </span>
      { options.length < 2 &&
        <span>You need at least two source books of your own to do this.</span>
      }
      <FlexBox direction="row">
        <FlexBox>
          <SelectObjectBox name="From" value={from_id} option_objects={options} disabled={running}
            onChange={(id: string) => loadFrom(id)}
          />
        </FlexBox>
        <FlexBox>
          <SelectObjectBox name="To" value={to_id} disabled={running}
            option_objects={options.filter(o => o._id !== from_id)}
            onChange={(id: string) => { setToId(id); setSelected([]); setSaving(null); }}
          />
        </FlexBox>
      </FlexBox>
      { loading && <span>Finding everything in { from_book?.name }...</span> }
      { from_book && to_book && contents &&
        <>
          { candidates.length === 0 ?
            <span>Everything in { from_book.name } is already in { to_book.name }.</span>
          :
            <>
              <CheckBox name={`Select All (${candidates.length})`} disabled={running}
                value={chosen.length === candidates.length}
                onChange={(changed: boolean) => toggle(candidates.map(keyOf), changed)}
              />
              <FlexBox direction="column" style={{ maxHeight: 300, overflowY: "auto" }}>
                { groups.map(group => (
                  <FlexBox key={group.data_type} direction="column">
                    <CheckBox name={`${pluralFor(group.data_type)} (${group.items.length})`} disabled={running}
                      value={group.items.every(o => selected.includes(keyOf(o)))}
                      onChange={(changed: boolean) => toggle(group.items.map(keyOf), changed)}
                    />
                    <FlexBox direction="column" style={{ paddingLeft: 24 }}>
                      { group.items.map(item => (
                        <CheckBox key={keyOf(item)} name={item.name} disabled={running}
                          value={selected.includes(keyOf(item))}
                          onChange={(changed: boolean) => toggle([keyOf(item)], changed)}
                        />
                      ))}
                    </FlexBox>
                  </FlexBox>
                ))}
              </FlexBox>
              <CheckBox name={`Also remove them from ${from_book.name}`} disabled={running}
                value={remove_from_old}
                onChange={(changed: boolean) => setRemoveFromOld(changed)}
              />
            </>
          }
        </>
      }
      <FlexBox direction="row">
        <FlexBox xs_expand={0}>
          <DandDButtonBox
            name={!from_book || !to_book ? "Choose Source Books" :
              `${remove_from_old ? "Move" : "Copy"} ${chosen.length} to ${to_book.name}`}
            disabled={running || !from_book || !to_book || chosen.length === 0}
            onClick={copy}
          />
        </FlexBox>
        <FlexBox xs_expand={0}>
          <DandDButtonBox name="Close" disabled={running} onClick={() => { setOpen(false); setSaving(null); }} />
        </FlexBox>
      </FlexBox>
      { saving &&
        <span>
          { running ?
            `Working... ${saving.done} / ${saving.total}` :
            `Done with ${saving.total - saving.failed.length} of ${saving.total}.${saving.failed.length > 0 ? ` Failed: ${saving.failed.join(", ")}` : ""}`
          }
        </span>
      }
    </FlexBox>
  );
}
